import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil, throttleTime} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';
import {AppState} from '../../../../core/reducers';
import {currentUser} from '../../../../core/auth';
import {MuscleDataService} from '../../../../services/muscle-data.service';
import {RewardService} from '../../../../services/reward/reward.service';
import {FirebaseService} from '../../../../services/firebase/firebase.service';
import {TrainingTemplateDataService} from '../../../../services/training-template-data/training-template-data.service';
import {TenantService} from 'src/app/core/tenant/tenant.service';

@Component({
  selector: 'kt-training-warnings',
  templateUrl: './training-warnings.component.html',
  styleUrls: ['./training-warnings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrainingWarningsComponent implements OnInit, OnDestroy {

  currentUser;
  userId = '';
  activeTrainingStep;
  currentArtefakt: number;
  showArtefaktSignal = true;
  showGreenColor = false;
  showYellowColor = false;
  showRedColor = false;
  artefactTresHold = '6';
  lighterColorInterval;
  destroy$ = new Subject();

  constructor(
    private cd: ChangeDetectorRef,
    private store: Store<AppState>,
    private muscleData: MuscleDataService,
    private reward: RewardService,
    private firebase: FirebaseService,
    private trainingData: TrainingTemplateDataService,
    public tenant: TenantService,
  ) { }

  ngOnInit(): void {
    this.getUserId();
    this.subscribeOnActiveTrainingStep();
    this.subscribeOnArtefaktValue();
    this.subscribeToArtefaktSignal();
  }

  ngOnDestroy(): void {
    clearInterval(this.lighterColorInterval);
    this.destroy$.next('');
    this.destroy$.complete();
  }

  getUserId() {
    this.store.pipe(select(currentUser), takeUntil(this.destroy$)).subscribe(user => {
      if (user) {
        this.currentUser = user;
        this.userId = user.id;
        this.subscribeOnArtefaktOption();
      }
    });
  }

  subscribeOnActiveTrainingStep() {
    this.trainingData.currentTemplate.pipe(takeUntil(this.destroy$)).subscribe((template) => {
      this.activeTrainingStep = template;
      this.cd.markForCheck();
    });
  }

  subscribeOnArtefaktValue() {
    this.muscleData.artefaktSubject.pipe(takeUntil(this.destroy$)).pipe(throttleTime(500)).subscribe((value) => {
      this.currentArtefakt = value;
      this.cd.markForCheck();
    });
  }

  subscribeOnArtefaktOption() {
    this.firebase.getUserScreenOptionsVideo(this.userId).pipe(takeUntil(this.destroy$)).subscribe((options: any) => {
      if (options) {
        this.showArtefaktSignal = options.showArtefakt;
        this.cd.markForCheck();
      }
    });
  }

  subscribeToArtefaktSignal() {
    this.lighterColorInterval = setInterval(() => {
      this.handleLighterColor();
    },500);
  }

  handleLighterColor() {
    if (this.currentArtefakt) {
      if (this.artefactTresHold === '2') {
        if (this.currentArtefakt < 1.8) {
          this.showGreenColor = true;
          this.showYellowColor = false;
          this.showRedColor = false;
          this.reward.lighterColor.next('#198200');
          this.muscleData.tooHightArtefact.next(false);
        } else if (this.currentArtefakt > 2) {
          this.showGreenColor = false;
          this.showYellowColor = false;
          this.reward.lighterColor.next('#FF0000');
          this.muscleData.tooHightArtefact.next(true);
          this.showRedColor = true;
        } else if (this.checkBetween(this.currentArtefakt, 1.8, 2)) {
          this.showYellowColor = true;
          this.showGreenColor = false;
          this.showRedColor = false;
          this.reward.lighterColor.next('#FFFF00');
          this.muscleData.tooHightArtefact.next(false);
        }
        this.cd.markForCheck();
        return;
      }
      if (this.artefactTresHold === '4') {
        if (this.currentArtefakt < 3) {
          this.showGreenColor = true;
          this.showYellowColor = false;
          this.showRedColor = false;
          this.reward.lighterColor.next('#198200');
          this.muscleData.tooHightArtefact.next(false);
        } else if (this.currentArtefakt > 4) {
          this.showGreenColor = false;
          this.showYellowColor = false;
          this.reward.lighterColor.next('#FF0000');
          this.muscleData.tooHightArtefact.next(true);
          this.showRedColor = true;
        } else if (this.checkBetween(this.currentArtefakt, 3, 4)) {
          this.showYellowColor = true;
          this.showGreenColor = false;
          this.showRedColor = false;
          this.reward.lighterColor.next('#FFFF00');
          this.muscleData.tooHightArtefact.next(false);
        }
        this.cd.markForCheck();
        return;
      }
      if (this.artefactTresHold === '6') {
        if (this.currentArtefakt < 4) {
          this.showGreenColor = true;
          this.showYellowColor = false;
          this.showRedColor = false;
          this.reward.lighterColor.next('#198200');
          this.muscleData.tooHightArtefact.next(false);
        } else if (this.currentArtefakt > 6) {
          this.showGreenColor = false;
          this.showYellowColor = false;
          this.reward.lighterColor.next('#FF0000');
          this.muscleData.tooHightArtefact.next(true);
          this.showRedColor = true;
        } else if (this.checkBetween(this.currentArtefakt, 4, 6)) {
          this.showYellowColor = true;
          this.showGreenColor = false;
          this.showRedColor = false;
          this.reward.lighterColor.next('#FFFF00');
          this.muscleData.tooHightArtefact.next(false);
        }
        this.cd.markForCheck();
        return;
      }
    }
  }

  checkBetween(value, min, max) {
    return value >= min && value <= max;
  }

  setArtefakt(event) {
    this.artefactTresHold = event.value;
  }

  triggerArtefakt() {
    this.showArtefaktSignal = !this.showArtefaktSignal;
    this.cd.markForCheck();
  }
}
